import * as XLSX from 'xlsx'

export interface ParsedRow {
    [key: string]: string | number | null
}

export interface ColumnMapping {
    name: number | null
    first_name_header: number | null
    last_name_header: number | null
    linkedinProfile: number | null
    email: number | null
    phone: number | null
}

/**
 * Parse CSV file content
 */
export const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0)
    return lines.map((line) => {
        // Handle quoted fields
        const result: string[] = []
        let current = ''
        let inQuotes = false

        for (let i = 0; i < line.length; i++) {
            const char = line[i]
            const nextChar = line[i + 1]

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    current += '"'
                    i++ // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                result.push(current.trim())
                current = ''
            } else {
                current += char
            }
        }
        // Add last field
        result.push(current.trim())
        return result
    })
}

/**
 * Parse XLSX file
 */
export const parseXLSX = async (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer)
                const workbook = XLSX.read(data, { type: 'array' })
                // Use first sheet
                const firstSheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[firstSheetName]
                // Convert to array of arrays
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                }) as (string | number)[][]
                // Convert all values to strings and ensure consistent array lengths
                const stringData: string[][] = jsonData.map((row) => {
                    // Ensure row is an array
                    if (!Array.isArray(row)) {
                        return []
                    }
                    return row.map((cell) => {
                        if (cell === null || cell === undefined) {
                            return ''
                        }
                        return String(cell)
                    })
                })
                resolve(stringData)
            } catch (error) {
                reject(error)
            }
        }
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsArrayBuffer(file)
    })
}

/**
 * Parse spreadsheet file (CSV or XLSX)
 */
export const parseSpreadsheet = async (file: File): Promise<string[][]> => {
    const fileName = file.name.toLowerCase()
    const fileExtension = fileName.split('.').pop()

    if (fileExtension === 'csv') {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string
                    const parsed = parseCSV(text)
                    resolve(parsed)
                } catch (error) {
                    reject(error)
                }
            }
            reader.onerror = () => reject(new Error('Failed to read CSV file'))
            reader.readAsText(file)
        })
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        return parseXLSX(file)
    } else {
        throw new Error('Unsupported file type. Please upload CSV or XLSX.')
    }
}

/**
 * Auto-detect column mapping based on header names
 */
export const autoDetectColumnMapping = (headers: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {
        name: null,
        first_name_header: null,
        last_name_header: null,
        linkedinProfile: null,
        email: null,
        phone: null,
    }

    headers.forEach((header, index) => {
        const lowerHeader = header.toLowerCase().trim()

        // Email detection
        if (
            !mapping.email &&
            (lowerHeader.includes('email') ||
                lowerHeader.includes('e-mail') ||
                lowerHeader === 'mail')
        ) {
            mapping.email = index
        }

        // Phone detection
        if (
            !mapping.phone &&
            (lowerHeader.includes('phone') ||
                lowerHeader.includes('tel') ||
                lowerHeader.includes('mobile') ||
                lowerHeader.includes('cell') ||
                lowerHeader.includes('telephone'))
        ) {
            mapping.phone = index
        }

        // Name detection - check for first/last name first, then generic name
        // Check for first name
        if (
            !mapping.first_name_header &&
            ((lowerHeader.includes('first') && lowerHeader.includes('name')) ||
                lowerHeader === 'first name' ||
                lowerHeader === 'firstname' ||
                lowerHeader === 'first_name' ||
                lowerHeader === 'fname')
        ) {
            mapping.first_name_header = index
        }
        // Check for last name
        else if (
            !mapping.last_name_header &&
            ((lowerHeader.includes('last') && lowerHeader.includes('name')) ||
                lowerHeader === 'last name' ||
                lowerHeader === 'lastname' ||
                lowerHeader === 'last_name' ||
                lowerHeader === 'lname' ||
                lowerHeader === 'surname')
        ) {
            mapping.last_name_header = index
        }
        // Check for generic name (only if not already set and not first/last)
        else if (
            !mapping.name &&
            !mapping.first_name_header &&
            !mapping.last_name_header &&
            (lowerHeader.includes('name') ||
                lowerHeader === 'full name' ||
                lowerHeader === 'fullname' ||
                lowerHeader === 'full_name')
        ) {
            mapping.name = index
        }

        // LinkedIn detection
        if (
            !mapping.linkedinProfile &&
            (lowerHeader.includes('linkedin') ||
                lowerHeader.includes('linked in') ||
                lowerHeader.includes('linkedin profile') ||
                lowerHeader.includes('linkedin url') ||
                lowerHeader.includes('linkedin_url') ||
                lowerHeader.includes('linkedin_profile'))
        ) {
            mapping.linkedinProfile = index
        }
    })

    return mapping
}

/**
 * Get rows from parsed data starting from a specific row index
 */
export const getRowsFromIndex = (
    data: string[][],
    startIndex: number,
    maxRows: number = 10,
): string[][] => {
    return data.slice(startIndex, startIndex + maxRows)
}

/**
 * Get header row from parsed data
 */
export const getHeaderRow = (
    data: string[][],
    headerRowIndex: number,
): string[] => {
    if (headerRowIndex >= 0 && headerRowIndex < data.length) {
        return data[headerRowIndex]
    }
    return []
}
